/**
 * Форматиране на числа с разделители за хиляди
 */
export const formatNumber = (num, decimals = 0) => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }
  
  return new Intl.NumberFormat('bg-BG', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

/**
 * Форматиране на валута
 */
export const formatCurrency = (amount, currency = 'BGN') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0,00 лв.';
  }
  
  return new Intl.NumberFormat('bg-BG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Форматиране на процент
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  
  return new Intl.NumberFormat('bg-BG', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
};

/**
 * Изчисляване на процент
 */
export const calculatePercent = (part, total) => {
  if (!total || total === 0) return 0;
  return (part / total) * 100;
};

/**
 * Форматиране на дата
 */
export const formatDate = (dateString, format = 'short') => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  
  if (isNaN(date.getTime())) return dateString;
  
  const options = format === 'short' 
    ? { day: '2-digit', month: '2-digit', year: 'numeric' }
    : { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' };
  
  return new Intl.DateTimeFormat('bg-BG', options).format(date);
};

/**
 * Съкращаване на текст
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength) + '...';
};

/**
 * Форматиране на телефонен номер
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Премахване на всички не-цифрови символи
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.length === 9) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
  }
  
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3');
  }
  
  if (cleaned.length === 12) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{3})/, '+$1 $2 $3 $4');
  }
  
  return phone;
};

/**
 * Форматиране на големи числа (K, M, B)
 */
export const formatLargeNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }
  
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  
  return formatNumber(num);
};

/**
 * Форматиране на размер на файл
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Конвертиране на първа буква на главна
 */
export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Конвертиране на snake_case към нормален текст
 */
export const snakeCaseToText = (str) => {
  if (!str) return '';
  return str
    .split('_')
    .map(word => capitalizeFirst(word))
    .join(' ');
};

/**
 * Форматиране на времеви период
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  const parts = [];
  
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  
  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs}s`);
  }
  
  return parts.join(' ');
};

/**
 * Валидация на имейл
 */
export const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

/**
 * Валидация на URL
 */
export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * Генериране на уникален ID
 */
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Декодиране на HTML entities
 */
export const decodeHtml = (html) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

/**
 * Форматиране на адрес
 */
export const formatAddress = (address) => {
  if (!address) return '';
  
  // Премахване на излишни интервали
  return address
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/([.,;:])\s*/g, '$1 ')
    .trim();
};

/**
 * Formats a number into a Bulgarian-friendly string, using "хил." for thousands
 * and "млн." for millions.
 * @param {number} num The number to format.
 * @returns {string} The formatted number string.
 */
export const formatInBulgarian = (num) => {
  if (num === null || num === undefined || isNaN(num)) {
    return "0";
  }

  if (num >= 1000000) {
    const value = num / 1000000;
    // Покажи един знак след запетаята, само ако не е .0
    const formatted = value.toFixed(1).replace(/\.0$/, "");
    return `${formatted} млн.`;
  }

  if (num >= 1000) {
    const value = num / 1000;
    const formatted = value.toFixed(0); // За хилядите не искаме десетични
    return `${formatted} хил.`;
  }

  return new Intl.NumberFormat("bg-BG").format(num);
};

export default {
  formatNumber,
  formatCurrency,
  formatPercent,
  calculatePercent,
  formatDate,
  truncateText,
  formatPhoneNumber,
  formatLargeNumber,
  formatFileSize,
  capitalizeFirst,
  snakeCaseToText,
  formatDuration,
  isValidEmail,
  isValidUrl,
  generateId,
  decodeHtml,
  formatAddress,
  formatInBulgarian,
};